import React, { useState, useRef, memo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClickOutside } from '../hooks';
import { ELEMENT_IDS } from '../utils/domConstants';
import { trackHeaderMenuOpened, trackNavigation, trackHireMeButtonClicked } from '../utils/analytics';
import './Header.css';

const Header = memo(() => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Define header titles for each page
  const getHeaderTitle = () => {
    switch (location.pathname) {
      case '/indie-dev-toolkit':
        return 'Indie Dev Toolkit';
      case '/about':
        return 'About';
      case '/contribute':
        return 'Contribute';
      default:
        return 'What\'s new in Dub Dub!'; // Landing page title
    }
  };


  const toggleMenu = useCallback(() => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);
    // Track when menu is opened
    if (newState) {
      trackHeaderMenuOpened();
    }
  }, [isMenuOpen]);

  const handleMenuItemClick = useCallback((item) => {
    setIsMenuOpen(false);
    const fromPage = location.pathname;
    if (item === 'Indie dev toolkit') {
      trackNavigation(fromPage, '/indie-dev-toolkit');
      navigate('/indie-dev-toolkit');
    } else if (item === 'About') {
      trackNavigation(fromPage, '/about');
      navigate('/about');
    }
  }, [navigate, location.pathname]);

  const handleTitleClick = useCallback(() => {
    const fromPage = location.pathname;
    if (fromPage !== '/') {
      trackNavigation(fromPage, '/');
    }
    navigate('/');
  }, [navigate, location.pathname]);

  const handleHireMeClick = useCallback(() => {
    trackHireMeButtonClicked('header');
    const hireFormSection = document.getElementById(ELEMENT_IDS.HIRE_FORM);
    if (hireFormSection) {
      hireFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Close menu when clicking outside using custom hook
  useClickOutside(menuRef, () => setIsMenuOpen(false));

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          {location.pathname !== '/' && (
            <button 
              className="back-button" 
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path 
                  d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <polyline 
                  points="9,22 9,12 15,12 15,22" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        
        <div className="header-center">
          <h1 className="header-title" onClick={handleTitleClick} style={{ cursor: 'pointer' }}>
            {location.pathname === '/' && (
              <img
                src="/favicon-192x192.png"
                alt="App Icon"
                className="header-icon"
              />
            )}
            <span className="title-line-1">{getHeaderTitle()}</span>
          </h1>
        </div>
        
        <div className="header-right">
          {/* Desktop navigation links - visible on larger screens */}
          <nav className="desktop-nav">
            <button
              className={`nav-link ${location.pathname === '/indie-dev-toolkit' ? 'active' : ''}`}
              onClick={() => handleMenuItemClick('Indie dev toolkit')}
            >
              Indie Dev Toolkit
            </button>
            <button
              className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
              onClick={() => handleMenuItemClick('About')}
            >
              About
            </button>
          </nav>

          {/* Mobile menu - visible only on smaller screens */}
          <div className="mobile-menu-container" ref={menuRef}>
            <button
              className={`menu-button ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
              aria-label="Open menu"
            >
              <span className="menu-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    className="menu-line menu-line-1"
                    d="M3 6h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    className="menu-line menu-line-2"
                    d="M3 12h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    className="menu-line menu-line-3"
                    d="M3 18h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>

            {isMenuOpen && (
              <div className="menu-dropdown">
                <button
                  className="menu-item"
                  onClick={() => handleMenuItemClick('Indie dev toolkit')}
                >
                  Indie dev toolkit
                </button>
                <button
                  className="menu-item"
                  onClick={() => handleMenuItemClick('About')}
                >
                  About
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
});

Header.displayName = 'Header';

export default Header;